import { FC, useEffect, useState, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useMyOrdersQuery, useSaveProductRatingMutation } from 'lib/home/generated/queries';
import { selectMealLineItems } from 'lib/home/util/products';
import { MyOrdersQuery } from 'lib/home/generated/types/api';
import { OrderHistoryDate } from './OrderHistoryDate';
import { ProductRatingModal, RatingInfoProps } from '../Modals/ProductRatingModal/ProductRatingModal';
import { ProductPayment } from './ProductPayment';
import { ProductRating } from '.';
import { Loading } from '..';

interface OrderHistoryListProps {
  orderDisplayId: number;
  setDisplayedOrderId: React.Dispatch<React.SetStateAction<number>>;
}

export const OrderHistoryList: FC<OrderHistoryListProps> = ({ orderDisplayId, setDisplayedOrderId }) => {
  const queryClient = useQueryClient();
  const { data: ordersData } = useMyOrdersQuery();
  const orders = ordersData?.customerOrders;

  const selectOrderFromOrderDisplayId = useCallback(
    id => (orders ? orders?.find(order => order.display_id === id) : null),
    [orders]
  );

  const [showOrder, setShowOrder] = useState<MyOrdersQuery['customerOrders'][number] | null>(
    selectOrderFromOrderDisplayId(orderDisplayId)
  );
  const [ratingModal, setRatingModal] = useState<{ isOpen: boolean; ratingInfo: RatingInfoProps | null }>({
    isOpen: false,
    ratingInfo: null
  });

  const { mutate: mutateProductRating } = useSaveProductRatingMutation({
    onSuccess: ({ saveProductRating }) => {
      setRatingModal({ isOpen: false, ratingInfo: null });

      queryClient.setQueryData(['MyOrders'], (myOrdersQuery: MyOrdersQuery) => {
        const orderIndex = myOrdersQuery.customerOrders.findIndex(
          order => order.id === saveProductRating.medusa_order_id
        );

        const itemIndex = myOrdersQuery.customerOrders[orderIndex].cart.items.findIndex(
          item => item.id === saveProductRating.medusa_line_item_id
        );

        myOrdersQuery.customerOrders[orderIndex].cart.items[itemIndex].product_rating = saveProductRating;
        return myOrdersQuery;
      });
    }
  });

  useEffect(() => {
    if (orderDisplayId || !orders || orders.length === 0) return;

    setDisplayedOrderId(orders[0].display_id);
  }, [orderDisplayId, orders, setDisplayedOrderId]);

  useEffect(() => {
    if (orders) setShowOrder(selectOrderFromOrderDisplayId(orderDisplayId));
  }, [orders, orderDisplayId, selectOrderFromOrderDisplayId]);

  if (!orders) return <Loading />;

  if (orders?.length === 0) return <p>There are no previous orders for this account.</p>;

  return (
    <div className="mt-8">
      <div className="lg:grid lg:grid-cols-2 bg-white">
        <div className="gap-x-2 grid grid-cols-2 sm:grid-cols-4 my-4 pl-4 pr-8">
          <div>Date</div>
          <div>Order Id</div>
          <div className="hidden sm:grid">Items</div>
          <div className="hidden sm:grid">Method</div>
        </div>
      </div>

      <hr />

      <div className="lg:grid lg:grid-cols-2">
        <div>
          {orders &&
            orders
              .sort((a, b) => (a.cart.completed_at > b.cart.completed_at ? -1 : 1))
              .map((order, index) => (
                <OrderHistoryDate
                  index={index}
                  order={order}
                  showOrder={showOrder}
                  setDisplayedOrderId={setDisplayedOrderId}
                  setRatingModal={setRatingModal}
                  key={order.id}
                />
              ))}
        </div>

        <div className="hidden lg:block">
          {showOrder && (
            <div className="bg-brand-cool-gray-20">
              <ProductPayment order={showOrder} />
              <h3 className="px-4 pb-2">Order Details</h3>
              {selectMealLineItems(showOrder.cart.items).map((item, index) => (
                <ProductRating
                  lineItem={item}
                  setRatingModal={setRatingModal}
                  orderId={showOrder.id}
                  index={index}
                  key={item.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <ProductRatingModal
        isOpen={ratingModal.isOpen}
        ratingInfo={ratingModal.ratingInfo}
        onAfterClose={() => setRatingModal({ isOpen: false, ratingInfo: null })}
        saveProductRating={saveProductRatingInput => mutateProductRating({ saveProductRatingInput })}
      />
    </div>
  );
};
